import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';
import { companyInfo } from '../data/mock';
import { FileText, Loader2, MessageCircle, Send } from 'lucide-react';

export const QuoteForm = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    vehicleType: '',
    serviceType: '',
    description: ''
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatWhatsAppMessage = () => {
    const vehicleTypeLabels = {
      'carro': 'Carro',
      'moto': 'Moto',
      'embarcacao': 'Embarcação',
      'comercial': 'Veículo Comercial',
      'outro': 'Outro'
    };

    const serviceTypeLabels = {
      'bate-chapas': 'Bate Chapas',
      'pintura-completa': 'Pintura Completa',
      'pintura-parcial': 'Pintura Parcial',
      'polimento': 'Polimento',
      'higienizacao': 'Higienização',
      'outro': 'Outro'
    };

    let message = `*Pedido de Orçamento - LMP PINTURAS*\n\n`;
    message += `*Nome:* ${formData.name}\n`;
    message += `*Telefone:* ${formData.phone}\n`;
    message += `*Tipo de Veículo:* ${vehicleTypeLabels[formData.vehicleType] || formData.vehicleType}\n`;
    message += `*Serviço:* ${serviceTypeLabels[formData.serviceType] || formData.serviceType}\n`;
    if (formData.description) message += `*Descrição:* ${formData.description}\n`;

    return encodeURIComponent(message);
  };

  const handleWhatsAppSubmit = () => {
    // Basic validation
    if (!formData.name || !formData.phone || !formData.vehicleType || !formData.serviceType) {
      toast.error('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    const message = formatWhatsAppMessage();
    const whatsappUrl = `https://wa.me/${companyInfo.whatsapp}?text=${message}`;
    
    // Open WhatsApp in new window
    window.open(whatsappUrl, '_blank');
    
    toast.success('Abrindo WhatsApp...');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.name || !formData.phone || !formData.vehicleType || !formData.serviceType) {
      toast.error('Por favor, preencha todos os campos obrigatórios');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(data.message);
        // Reset form
        setFormData({
          name: '',
          phone: '',
          vehicleType: '',
          serviceType: '',
          description: ''
        });
      } else {
        toast.error(data.detail || 'Erro ao enviar pedido. Tente novamente.');
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Erro ao enviar pedido. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="py-20 bg-slate-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-12">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider mb-2 block">
              Peça seu Orçamento
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-4">
              Orçamento Grátis Online
            </h2>
            <p className="text-lg text-slate-600">
              Preencha o formulário e receba um orçamento personalizado sem compromisso
            </p>
          </div>

          <Card className="shadow-xl border-slate-200">
            <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center">
                  <FileText size={24} />
                </div>
                <div>
                  <CardTitle className="text-2xl">Formulário de Orçamento</CardTitle>
                  <CardDescription className="text-blue-100">
                    Resposta em até 24 horas
                  </CardDescription>
                </div>
              </div>
            </CardHeader>

            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-700 font-medium">
                    Nome Completo *
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Seu nome"
                    required
                    className="border-slate-300 focus:border-blue-500"
                  />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-700 font-medium">
                    Telefone *
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+351 XXX XXX XXX"
                    required
                    className="border-slate-300 focus:border-blue-500"
                  />
                </div>

                {/* Vehicle Type and Service Type Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="vehicleType" className="text-slate-700 font-medium">
                      Tipo de Veículo *
                    </Label>
                    <Select 
                      value={formData.vehicleType}
                      onValueChange={(value) => handleChange('vehicleType', value)}
                      required
                    >
                      <SelectTrigger className="border-slate-300 focus:border-blue-500">
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="carro">Carro</SelectItem>
                        <SelectItem value="moto">Moto</SelectItem>
                        <SelectItem value="embarcacao">Embarcação</SelectItem>
                        <SelectItem value="comercial">Veículo Comercial</SelectItem>
                        <SelectItem value="outro">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="serviceType" className="text-slate-700 font-medium">
                      Serviço Desejado *
                    </Label>
                    <Select 
                      value={formData.serviceType}
                      onValueChange={(value) => handleChange('serviceType', value)}
                      required
                    >
                      <SelectTrigger className="border-slate-300 focus:border-blue-500">
                        <SelectValue placeholder="Selecione o serviço" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="bate-chapas">Bate Chapas</SelectItem>
                        <SelectItem value="pintura-completa">Pintura Completa</SelectItem>
                        <SelectItem value="pintura-parcial">Pintura Parcial</SelectItem>
                        <SelectItem value="polimento">Polimento</SelectItem>
                        <SelectItem value="higienizacao">Higienização</SelectItem>
                        <SelectItem value="outro">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description" className="text-slate-700 font-medium">
                    Descrição do Serviço
                  </Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="Descreva o serviço que necessita..."
                    rows={5}
                    className="border-slate-300 focus:border-blue-500 resize-none"
                  />
                </div>

                {/* Submit Buttons */}
                <div className="space-y-3">
                  <Button 
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white text-lg py-6 shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 animate-spin" size={20} />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2" size={20} />
                        Solicitar Orçamento Grátis
                      </>
                    )}
                  </Button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-slate-300" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-slate-500">ou</span>
                    </div>
                  </div>

                  <Button 
                    type="button"
                    onClick={handleWhatsAppSubmit}
                    className="w-full bg-green-600 hover:bg-green-700 text-white text-lg py-6 shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    <MessageCircle className="mr-2" size={20} />
                    Enviar via WhatsApp
                  </Button>
                </div>

                <p className="text-center text-sm text-slate-500 mt-4">
                  * Campos obrigatórios
                </p>
              </form>
            </CardContent>
          </Card>

          {/* Contact Info Box */}
          <div className="mt-8 text-center bg-white rounded-xl p-6 shadow-lg border border-slate-200">
            <p className="text-slate-600 mb-3">Prefere contactar diretamente?</p>
            <a 
              href="tel:+351963241522"
              className="text-blue-600 hover:text-blue-700 font-bold text-xl transition-colors"
            >
              +351 963 241 522
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
